import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'

import Logo from '@/components/Header/Logo'

import { PAGE_PATHS } from '@/services/paths'
import Link from 'next/link'

import {
  changeClientLanguage,
  getLocalizedZendeskLink
} from '@/services/language'

import * as S from './styles'

type MenuItem = {
  label: string
  href?: string
  external?: boolean
}

type MenuColumn = MenuItem[]

type DropdownType = 'resources' | 'teaching' | 'contents' | 'language' | null

const LANGUAGE_OPTIONS = [
  { code: 'pt', label: 'PT', fullLabel: 'Português' },
  { code: 'en', label: 'EN', fullLabel: 'English' },
  { code: 'es', label: 'ES', fullLabel: 'Español' }
]

const LanguageGlobeIcon = ({ color }: { color: string }) => {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 17 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M14.0443 12.2432C13.1287 11.9386 12.1442 11.7111 11.1081 11.5638C11.2492 10.7352 11.3243 9.86693 11.3482 8.9781H15.1756C15.09 10.1835 14.685 11.2989 14.0443 12.2432ZM10.1419 14.9829C10.4603 14.2136 10.7209 13.3821 10.9212 12.5062C11.7809 12.6334 12.6052 12.8213 13.3859 13.068C12.5234 13.9874 11.4036 14.664 10.1419 14.9829ZM8.96423 15.1703C8.81028 15.1813 8.6568 15.1937 8.49998 15.1937C8.34315 15.1937 8.19015 15.1813 8.03572 15.1703C7.62884 14.3231 7.29941 13.3802 7.05556 12.3795C7.53082 12.3379 8.01229 12.3135 8.49998 12.3135C8.98766 12.3135 9.46914 12.3379 9.94487 12.3795C9.70055 13.3802 9.37112 14.3231 8.96423 15.1703ZM3.61402 13.068C4.3948 12.8213 5.21908 12.6334 6.07875 12.5062C6.27909 13.3821 6.53966 14.2136 6.85809 14.9829C5.59632 14.664 4.47703 13.9874 3.61402 13.068ZM2.95564 12.2432C2.31495 11.2989 1.90998 10.1835 1.82439 8.9781H5.65178C5.67569 9.86693 5.75076 10.7352 5.8918 11.5638C4.85571 11.7111 3.87125 11.9386 2.95564 12.2432ZM2.95564 4.75674C3.87125 5.06131 4.85571 5.28936 5.8918 5.43614C5.75076 6.26473 5.67569 7.13302 5.65178 8.02185H1.82439C1.90998 6.8165 2.31495 5.70104 2.95564 4.75674ZM6.85809 2.01707C6.53966 2.78637 6.27909 3.61783 6.07875 4.49375C5.21908 4.36705 4.3948 4.17867 3.61402 3.93196C4.47703 3.01253 5.59632 2.33598 6.85809 2.01707ZM8.03572 1.82966C8.19015 1.81914 8.34315 1.80623 8.49998 1.80623C8.6568 1.80623 8.81028 1.81914 8.96423 1.82966C9.37112 2.6769 9.70055 3.62023 9.94487 4.62046C9.46914 4.66206 8.98766 4.68644 8.49998 4.68644C8.01229 4.68644 7.53082 4.66206 7.05556 4.62046C7.29941 3.62023 7.62884 2.6769 8.03572 1.82966ZM10.3905 8.9781C10.3647 9.82008 10.2882 10.6482 10.1442 11.4433C9.60588 11.3965 9.05843 11.3687 8.49998 11.3687C7.94153 11.3687 7.39407 11.3965 6.85618 11.4433C6.71179 10.6482 6.63529 9.82008 6.60995 8.9781H10.3905ZM6.60995 8.02185C6.63529 7.17987 6.71179 6.35176 6.85618 5.55664C7.39407 5.60349 7.94153 5.63123 8.49998 5.63123C9.05843 5.63123 9.60588 5.60349 10.1438 5.55664C10.2882 6.35176 10.3647 7.17987 10.3905 8.02185H6.60995ZM13.3859 3.93196C12.6052 4.17867 11.7809 4.36705 10.9212 4.49375C10.7209 3.61783 10.4603 2.78637 10.1419 2.01707C11.4036 2.33598 12.5234 3.01253 13.3859 3.93196ZM14.0443 4.75674C14.685 5.70104 15.09 6.8165 15.1756 8.02185H11.3482C11.3243 7.13302 11.2492 6.26473 11.1081 5.43614C12.1442 5.28936 13.1287 5.06131 14.0443 4.75674ZM8.49998 0.849976C4.27526 0.849976 0.849976 4.27479 0.849976 8.49998C0.849976 12.7252 4.27526 16.15 8.49998 16.15C12.7247 16.15 16.15 12.7252 16.15 8.49998C16.15 4.27479 12.7247 0.849976 8.49998 0.849976Z"
        fill={color}
      />
    </svg>
  )
}

const HeaderLandingV2 = () => {
  const { i18n, t } = useTranslation()
  const router = useRouter()

  const [openDropdown, setOpenDropdown] = useState<DropdownType>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState<{ [key: string]: boolean }>({
    resources: false,
    teaching: false,
    contents: false,
    language: false
  })

  const normalize = (path: string) => {
    const clean = path.split('?')[0].split('#')[0]
    const locale = router.locale
    if (locale && locale !== router.defaultLocale) {
      const prefix = `/${locale}`
      if (clean.startsWith(prefix + '/')) return clean.slice(prefix.length)
      if (clean === prefix) return '/'
    }
    return clean
  }

  const currentPath = normalize(router.asPath)

  const anySectionOpen = useMemo(
    () => Object.values(mobileOpen).some(v => v),
    [mobileOpen]
  )

  const isActive = (basePath: string) => {
    if (basePath === '/') return currentPath === '/'
    const base = basePath.split('?')[0].split('#')[0].replace(/\/$/, '')
    return currentPath === base || currentPath.startsWith(base + '/')
  }

  const isActiveGroup = (paths: string[]) => paths.some(path => isActive(path))

  const containerRef = useRef<HTMLDivElement>(null)

  // Derived from reactive i18n state — updates on language change
  const langDisplay = useMemo(
    () => (i18n.language?.split('-')[0] || 'pt').toUpperCase(),
    [i18n.language]
  )

  // Novos itens do menu e dropdowns — cada dropdown é um array de colunas
  const resourcesItems: MenuColumn[] = useMemo(
    () => [
      [
        {
          label: t('titles.collections'),
          href: PAGE_PATHS.resources.collections
        },
        { label: t('titles.app'), href: PAGE_PATHS.resources.app },
        {
          label: t('titles.integration'),
          href: PAGE_PATHS.resources.integration
        }
      ],
      [
        { label: t('titles.viewer3d'), href: PAGE_PATHS.resources.viewer3d },
        {
          label: t('titles.slideViewer'),
          href: PAGE_PATHS.resources.slideViewer
        },
        {
          label: t('titles.radiologyViewer'),
          href: PAGE_PATHS.resources.radiologyViewer
        }
      ]
    ],
    [t]
  )

  const teachingItems: MenuColumn[] = useMemo(
    () => [
      [{ label: t('titles.forTeachers'), href: PAGE_PATHS.forTeachers }],
      [{ label: t('titles.forStudents'), href: PAGE_PATHS.forStudents }]
    ],
    [t]
  )

  const contentsItems: MenuColumn[] = useMemo(
    () => [
      [
        {
          label: t('titles.completeAnatomy'),
          href: PAGE_PATHS.contentsPages.completeAnatomy
        },
        {
          label: t('titles.systemicAnatomy'),
          href: PAGE_PATHS.contentsPages.systemicAnatomy
        },
        {
          label: t('titles.topographicAnatomy'),
          href: PAGE_PATHS.contentsPages.topographicAnatomy
        },
        {
          label: t('titles.biochemistry'),
          href: PAGE_PATHS.contentsPages.biochemistry
        },
        {
          label: t('titles.cytology'),
          href: PAGE_PATHS.contentsPages.cytology
        },
        {
          label: t('titles.embryology'),
          href: PAGE_PATHS.contentsPages.embryology
        }
      ],
      [
        {
          label: t('titles.physiology'),
          href: PAGE_PATHS.contentsPages.physiology
        },
        {
          label: t('titles.histology'),
          href: PAGE_PATHS.contentsPages.histology
        },
        {
          label: t('titles.histopathology'),
          href: PAGE_PATHS.contentsPages.histopathology
        },
        {
          label: t('titles.pathology'),
          href: PAGE_PATHS.contentsPages.pathology
        },
        {
          label: t('titles.radiology'),
          href: PAGE_PATHS.contentsPages.radiology
        }
      ]
    ],
    [t]
  )

  // Close desktop dropdown on click outside the active dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!openDropdown) return

      const target = e.target as Node

      // If we clicked a button that toggles a dropdown, let the button's onClick handle it
      if (
        target instanceof Element &&
        target.closest('[data-dropdown-toggle]')
      ) {
        return
      }

      // If click is outside the open dropdown container, close it
      const activeDropdownEl = containerRef.current?.querySelector(
        `[data-dropdown-id="${openDropdown}"]`
      )

      if (activeDropdownEl && !activeDropdownEl.contains(target)) {
        setOpenDropdown(null)
      }
    }

    window.addEventListener('click', handler)
    return () => window.removeEventListener('click', handler)
  }, [openDropdown])

  // Close dropdowns and mobile menu on route changes
  useEffect(() => {
    const handleRouteChange = () => {
      setOpenDropdown(null)
      setMobileMenuOpen(false)
    }
    router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router])

  // Close desktop dropdown on Escape
  useEffect(() => {
    if (!openDropdown) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenDropdown(null)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [openDropdown])

  // Mobile: lock scroll and close on Escape
  useEffect(() => {
    if (!mobileMenuOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false)
    }
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handler)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handler)
    }
  }, [mobileMenuOpen])

  const toggleDropdown = (key: DropdownType) =>
    setOpenDropdown(prev => (prev === key ? null : key))

  const toggleMobileSection = (key: string) =>
    setMobileOpen(prev => {
      const next: { [key: string]: boolean } = {}
      Object.keys(prev).forEach(k => {
        next[k] = false
      })
      next[key] = !prev[key]
      return next
    })

  const renderDropdownItems = (columns: MenuColumn[]) => (
    <S.DropdownMenu role="menu" columnCount={columns.length}>
      {columns.map((col, colIdx) => (
        <S.DropdownColumn key={colIdx}>
          {col.map(item =>
            item.href ? (
              <Link
                key={item.label}
                href={item.href}
                role="menuitem"
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noopener noreferrer' : undefined}
                onClick={() => setOpenDropdown(null)}
              >
                {item.label}
              </Link>
            ) : (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                onClick={() => setOpenDropdown(null)}
              >
                {item.label}
              </button>
            )
          )}
        </S.DropdownColumn>
      ))}
    </S.DropdownMenu>
  )

  const chevron = (id: DropdownType) => (
    <span
      className="material-icons-round"
      aria-hidden="true"
      style={{ fontSize: '18px' }}
    >
      {openDropdown === id ? 'expand_less' : 'expand_more'}
    </span>
  )

  const globeColor =
    openDropdown === 'language' ? 'var(--blue)' : 'var(--grey3)'

  return (
    <>
      <S.HeaderWrapper>
        <S.HeaderContainer ref={containerRef}>
          <Logo color="#21105D" />

          {/* Desktop navigation — semantic landmark with accessible label */}
          <S.NavDesktop aria-label={t('titles.home')}>
            <S.MenuLink
              as={Link}
              href={PAGE_PATHS.landing}
              active={isActive(PAGE_PATHS.landing)}
              aria-current={isActive(PAGE_PATHS.landing) ? 'page' : undefined}
            >
              {t('titles.home')}
            </S.MenuLink>

            <S.DropdownContainer data-dropdown-id="resources">
              <S.MenuButton
                type="button"
                data-dropdown-toggle
                onClick={() => toggleDropdown('resources')}
                active={isActive(PAGE_PATHS.resources.base)}
                aria-expanded={openDropdown === 'resources'}
                aria-haspopup="true"
              >
                {t('titles.resources')}
                {chevron('resources')}
              </S.MenuButton>
              {openDropdown === 'resources' &&
                renderDropdownItems(resourcesItems)}
            </S.DropdownContainer>

            <S.DropdownContainer data-dropdown-id="teaching">
              <S.MenuButton
                type="button"
                data-dropdown-toggle
                onClick={() => toggleDropdown('teaching')}
                active={isActiveGroup([
                  PAGE_PATHS.forTeachers,
                  PAGE_PATHS.forStudents
                ])}
                aria-expanded={openDropdown === 'teaching'}
                aria-haspopup="true"
              >
                {t('titles.teaching')}
                {chevron('teaching')}
              </S.MenuButton>
              {openDropdown === 'teaching' &&
                renderDropdownItems(teachingItems)}
            </S.DropdownContainer>

            <S.DropdownContainer data-dropdown-id="contents">
              <S.MenuButton
                type="button"
                data-dropdown-toggle
                onClick={() => toggleDropdown('contents')}
                active={isActive(PAGE_PATHS.contents)}
                aria-expanded={openDropdown === 'contents'}
                aria-haspopup="true"
              >
                {t('titles.contents')}
                {chevron('contents')}
              </S.MenuButton>
              {openDropdown === 'contents' &&
                renderDropdownItems(contentsItems)}
            </S.DropdownContainer>

            <S.MenuLink
              href={getLocalizedZendeskLink()}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('titles.support')}
            </S.MenuLink>
          </S.NavDesktop>

          <S.RightActions>
            <S.DesktopActions>
              <S.RequestButton
                href={PAGE_PATHS.contact}
                id="btn_header_v2_request"
              >
                {t('buttons.requestPresentation')}
              </S.RequestButton>
              <S.LoginButton href={PAGE_PATHS.signin} id="btn_header_v2_login">
                {t('titles.login')}
              </S.LoginButton>
            </S.DesktopActions>

            {/* Language switcher — desktop only */}
            <S.LanguageContainer data-dropdown-id="language">
              <S.LanguageButton
                type="button"
                data-dropdown-toggle
                active={openDropdown === 'language'}
                onClick={() => toggleDropdown('language')}
                aria-expanded={openDropdown === 'language'}
                aria-label={t('titles.language')}
              >
                <S.LanguageIcon>
                  <LanguageGlobeIcon color={globeColor} />
                </S.LanguageIcon>
                {langDisplay}
              </S.LanguageButton>

              {openDropdown === 'language' && (
                <S.DropdownMenu role="menu" isLanguage>
                  <S.DropdownColumn>
                    {LANGUAGE_OPTIONS.map(item => (
                      <button
                        key={item.code}
                        type="button"
                        role="menuitem"
                        lang={item.code}
                        onClick={() => {
                          changeClientLanguage(item.code, false)
                          setOpenDropdown(null)
                        }}
                      >
                        {item.fullLabel}
                      </button>
                    ))}
                  </S.DropdownColumn>
                </S.DropdownMenu>
              )}
            </S.LanguageContainer>

            {/* Mobile: CTA + hamburger */}
            <S.MobileRequestButton
              href={PAGE_PATHS.contact}
              id="btn_header_v2_request_mobile"
            >
              {t('buttons.requestPresentation')}
            </S.MobileRequestButton>

            <S.MobileMenuButton
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              aria-label={t('titles.home')}
              aria-expanded={mobileMenuOpen}
            >
              <span className="material-icons-round" aria-hidden="true">
                menu
              </span>
            </S.MobileMenuButton>
          </S.RightActions>
        </S.HeaderContainer>
      </S.HeaderWrapper>

      {/* Mobile fullscreen overlay */}
      <S.MobileOverlay
        open={mobileMenuOpen}
        role="dialog"
        aria-modal="true"
        aria-label={t('titles.home')}
      >
        <S.MobileTop>
          <S.MobileClose
            type="button"
            onClick={() => setMobileMenuOpen(false)}
            aria-label={t('titles.home')}
          >
            <span className="material-icons-round" aria-hidden="true">
              close
            </span>
          </S.MobileClose>
        </S.MobileTop>

        <S.MobileNav>
          <Link
            href={PAGE_PATHS.landing}
            data-active={isActive(PAGE_PATHS.landing) && !anySectionOpen}
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('titles.home')}
          </Link>

          <button
            type="button"
            aria-expanded={mobileOpen.resources}
            onClick={() => toggleMobileSection('resources')}
            data-active={
              mobileOpen.resources ||
              (isActive(PAGE_PATHS.resources.base) && !anySectionOpen)
            }
          >
            {t('titles.resources')}
          </button>
          {mobileOpen.resources && (
            <S.MobileDropdown>
              {resourcesItems.flat().map(item =>
                item.href ? (
                  <Link
                    key={item.label}
                    href={item.href}
                    data-active={isActive(item.href)}
                  >
                    {item.label}
                  </Link>
                ) : null
              )}
            </S.MobileDropdown>
          )}

          <button
            type="button"
            aria-expanded={mobileOpen.teaching}
            onClick={() => toggleMobileSection('teaching')}
            data-active={
              mobileOpen.teaching ||
              (isActiveGroup([
                PAGE_PATHS.forTeachers,
                PAGE_PATHS.forStudents
              ]) &&
                !anySectionOpen)
            }
          >
            {t('titles.teaching')}
          </button>
          {mobileOpen.teaching && (
            <S.MobileDropdown>
              {teachingItems.flat().map(item =>
                item.href ? (
                  <Link
                    key={item.label}
                    href={item.href}
                    data-active={isActive(item.href)}
                  >
                    {item.label}
                  </Link>
                ) : null
              )}
            </S.MobileDropdown>
          )}

          <button
            type="button"
            aria-expanded={mobileOpen.contents}
            onClick={() => toggleMobileSection('contents')}
            data-active={
              mobileOpen.contents ||
              (isActive(PAGE_PATHS.contents) && !anySectionOpen)
            }
          >
            {t('titles.contents')}
          </button>
          {mobileOpen.contents && (
            <S.MobileDropdown>
              {contentsItems.flat().map(item =>
                item.href ? (
                  <Link
                    key={item.label}
                    href={item.href}
                    data-active={isActive(item.href)}
                  >
                    {item.label}
                  </Link>
                ) : null
              )}
            </S.MobileDropdown>
          )}

          <a
            href={getLocalizedZendeskLink()}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('titles.support')}
          </a>

          <button
            type="button"
            aria-expanded={mobileOpen.language}
            onClick={() => toggleMobileSection('language')}
            data-active={!!mobileOpen.language}
          >
            <S.MobileLangButtonContent>
              <LanguageGlobeIcon color="var(--grey3)" />
              {LANGUAGE_OPTIONS.find(
                opt => opt.code === (i18n.language?.split('-')[0] || 'pt')
              )?.fullLabel ?? langDisplay}
            </S.MobileLangButtonContent>
          </button>
          {mobileOpen.language && (
            <S.MobileDropdown>
              {LANGUAGE_OPTIONS.map(item => (
                <button
                  key={item.code}
                  type="button"
                  lang={item.code}
                  onClick={() => {
                    changeClientLanguage(item.code, false)
                    setMobileMenuOpen(false)
                  }}
                >
                  {item.fullLabel}
                </button>
              ))}
            </S.MobileDropdown>
          )}
        </S.MobileNav>

        <S.MobileBottom>
          <S.RequestButton href={PAGE_PATHS.contact}>
            {t('buttons.requestPresentation')}
          </S.RequestButton>
          <S.LoginButton href={PAGE_PATHS.signin}>
            {t('titles.login')}
          </S.LoginButton>
        </S.MobileBottom>
      </S.MobileOverlay>

      <S.PageOffset />
    </>
  )
}

export default HeaderLandingV2
